import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Divider,
  Stack,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelCircleIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";

const Attendance1 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [year, setYear] = useState("2025");
  const [month, setMonth] = useState("January");
  const [selectedSection, setSelectedSection] = useState("Attendance");
  const [tableData, setTableData] = useState([
    {
      date: "2024-01-15",
      inTime: "09:00",
      outTime: "18:00",
      workedHours: "9.0",
      regularizedInTime: "09:00",
      regularizedOutTime: "18:00",
      regularizedWorkedHrs: "9.0",
      attendanceStatus: "Present",
      shift: "Day",
      manager: "John Doe",
      projectId: "PROJ-001",
      appliedHrsForBilling: "8.0",
      approvedHrsForBilling: "8.0",
      billableDays: "1",
      remarks: "Completed tasks",
    },
    {
      date: "2024-01-16",
      inTime: "09:15",
      outTime: "17:45",
      workedHours: "8.5",
      regularizedInTime: "09:15",
      regularizedOutTime: "17:45",
      regularizedWorkedHrs: "8.5",
      attendanceStatus: "Present",
      shift: "Day",
      manager: "Jane Smith",
      projectId: "PROJ-002",
      appliedHrsForBilling: "8.0",
      approvedHrsForBilling: "8.0",
      billableDays: "1",
      remarks: "Meeting with client",
    },
  ]);
  
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [newRow, setNewRow] = useState({
    date: "",
    inTime: "",
    outTime: "",
    workedHours: "",
    regularizedInTime: "",
    regularizedOutTime: "",
    regularizedWorkedHrs: "",
    attendanceStatus: "Present",
    shift: "Day",
    manager: "",
    projectId: "",
    appliedHrsForBilling: "",
    approvedHrsForBilling: "",
    billableDays: "",
    remarks: "",
  });

  // Filtered data based on search and filter
  const filteredData = tableData.filter((row) => {
    const matchesSearch = 
      row.date?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.attendanceStatus?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.projectId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.manager?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === "all" || row.attendanceStatus === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Calculate summary statistics
  const summaryStats = {
    totalDays: filteredData.length,
    presentDays: filteredData.filter(row => row.attendanceStatus === "Present").length,
    totalHours: filteredData.reduce((sum, row) => sum + parseFloat(row.workedHours || 0), 0).toFixed(1),
    billableHours: filteredData.reduce((sum, row) => sum + parseFloat(row.approvedHrsForBilling || 0), 0).toFixed(1),
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(filteredData.map((_, index) => index));
      return;
    }
    setSelectedRows([]);
  };

  const handleRowSelect = (event, index) => {
    if (event.target.checked) {
      setSelectedRows([...selectedRows, index]);
    } else {
      setSelectedRows(selectedRows.filter((i) => i !== index));
    }
  };

  const handleEdit = (index) => {
    setEditingId(index);
    if (isMobile) {
      setCurrentRow(filteredData[index]);
      setOpenDialog(true);
    }
  };

  const handleSave = (index) => {
    setEditingId(null);
    if (isMobile) {
      setOpenDialog(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleDelete = (index) => {
    setTableData(tableData.filter((_, i) => i !== index));
    setSelectedRows(
      selectedRows
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i))
    );
  };

  const handleAddNew = () => {
    setIsAdding(true);
    if (isMobile) {
      setOpenDialog(true);
    }
  };

  const handleSaveNew = () => {
    setTableData([newRow, ...tableData]);
    setIsAdding(false);
    setNewRow({
      date: "",
      inTime: "",
      outTime: "",
      workedHours: "",
      regularizedInTime: "",
      regularizedOutTime: "",
      regularizedWorkedHrs: "",
      attendanceStatus: "Present",
      shift: "Day",
      manager: "",
      projectId: "",
      appliedHrsForBilling: "",
      approvedHrsForBilling: "",
      billableDays: "",
      remarks: "",
    });
    if (isMobile) {
      setOpenDialog(false);
    }
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewRow({
      date: "",
      inTime: "",
      outTime: "",
      workedHours: "",
      regularizedInTime: "",
      regularizedOutTime: "",
      regularizedWorkedHrs: "",
      attendanceStatus: "Present",
      shift: "Day",
      manager: "",
      projectId: "",
      appliedHrsForBilling: "",
      approvedHrsForBilling: "",
      billableDays: "",
      remarks: "",
    });
    if (isMobile) {
      setOpenDialog(false);
    }
  };

  const handleInputChange = (e, index, field) => {
    const updatedData = [...tableData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: e.target.value,
    };
    setTableData(updatedData);
  };

  const handleInputChangeNew = (e, field) => {
    setNewRow({
      ...newRow,
      [field]: e.target.value,
    });
  };

  const handleExport = () => {
    console.log("Exporting data...");
  };

  const handleViewDetails = (row) => {
    setCurrentRow(row);
    setOpenDialog(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "Present": return "success";
      case "Absent": return "error";
      case "Late": return "warning";
      case "Leave": return "info";
      default: return "default";
    }
  };

  // Mobile Card Component
  const MobileAttendanceCard = ({ row, index }) => (
    <Card sx={{ mb: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarIcon fontSize="small" />
              {row.date}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <ScheduleIcon fontSize="small" />
              {row.inTime} - {row.outTime}
            </Typography>
          </Box>
          <Chip 
            label={row.attendanceStatus} 
            size="small"
            color={getStatusColor(row.attendanceStatus)}
            sx={{ fontWeight: 500 }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Hours Worked
            </Typography>
            <Typography variant="body2" fontWeight={500} display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon fontSize="small" />
              {row.workedHours}h
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Billable Hours
            </Typography>
            <Typography variant="body2" fontWeight={500} display="flex" alignItems="center" gap={0.5}>
              <CheckCircleIcon fontSize="small" />
              {row.approvedHrsForBilling}h
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Project
            </Typography>
            <Typography variant="body2" fontWeight={500} display="flex" alignItems="center" gap={0.5}>
              <WorkIcon fontSize="small" />
              {row.projectId}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Manager
            </Typography>
            <Typography variant="body2" fontWeight={500} display="flex" alignItems="center" gap={0.5}>
              <PersonIcon fontSize="small" />
              {row.manager}
            </Typography>
          </Grid>
        </Grid>

        {row.remarks && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Remarks
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              "{row.remarks}"
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleEdit(index)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  // Summary Cards
  const SummaryCard = ({ icon, title, value, color }) => (
    <Card sx={{ 
      background: `linear-gradient(135deg, ${color}20, ${color}10)`,
      border: `1px solid ${color}30`,
      borderRadius: 2,
      height: "100%"
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ 
            bgcolor: `${color}20`, 
            color: color,
            width: 40, 
            height: 40 
          }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} color={color}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      bgcolor: "#f8fafc", 
      minHeight: "100vh",
      py: { xs: 2, sm: 3, md: 4 }
    }}>
      <Box sx={{ 
        maxWidth: "100%", 
        mx: "auto",
        px: { xs: 1.5, sm: 2, md: 3 }
      }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
            Attendance Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage employee attendance records for {month} {year}
          </Typography>
        </Box>

        {/* Filters and Controls */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year">
                      {["2025", "2024", "2023", "2022"].map((yr) => (
                        <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Month">
                      {[
                        "January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"
                      ].map((mth) => (
                        <MenuItem key={mth} value={mth}>{mth}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="Present">Present</MenuItem>
                      <MenuItem value="Absent">Absent</MenuItem>
                      <MenuItem value="Late">Late</MenuItem>
                      <MenuItem value="Leave">Leave</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", gap: 2, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 200 }}
                  />

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    sx={{
                      bgcolor: "#C38238",
                      "&:hover": { bgcolor: "#B17330" },
                      px: 3
                    }}
                  >
                    Export
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddNew}
                    sx={{ px: 3 }}
                  >
                    Add New
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              icon={<CalendarIcon />}
              title="Total Days"
              value={summaryStats.totalDays}
              color="#7564e6"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              icon={<CheckCircleIcon />}
              title="Present Days"
              value={summaryStats.presentDays}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              icon={<AccessTimeIcon />}
              title="Total Hours"
              value={`${summaryStats.totalHours}h`}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              icon={<WorkIcon />}
              title="Billable Hours"
              value={`${summaryStats.billableHours}h`}
              color="#9c27b0"
            />
          </Grid>
        </Grid>

        {/* Data Display */}
        {isMobile ? (
          /* Mobile View - Card Layout */
          <Box>
            {filteredData.length === 0 ? (
              <Card sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body1">
                  No attendance records found
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />} 
                  onClick={handleAddNew}
                  sx={{ mt: 2 }}
                >
                  Add First Record
                </Button>
              </Card>
            ) : (
              filteredData.map((row, index) => (
                <MobileAttendanceCard key={index} row={row} index={index} />
              ))
            )}
          </Box>
        ) : (
          /* Desktop/Tablet View - Table Layout */
          <Card sx={{ borderRadius: 2, boxShadow: 2, overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: "#f5f7fa" }}>
                      <Checkbox
                        indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                        checked={filteredData.length > 0 && selectedRows.length === filteredData.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>In Time</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>Out Time</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>Hours</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>Project</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f7fa", fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Add New Row */}
                  {isAdding && (
                    <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                      <TableCell padding="checkbox">
                        <Checkbox disabled />
                      </TableCell>
                      {["date", "inTime", "outTime", "workedHours", "attendanceStatus", "projectId"].map((field) => (
                        <TableCell key={field}>
                          <TextField
                            fullWidth
                            size="small"
                            value={newRow[field]}
                            onChange={(e) => handleInputChangeNew(e, field)}
                            placeholder={field === "date" ? "YYYY-MM-DD" : ""}
                          />
                        </TableCell>
                      ))}
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" color="primary" onClick={handleSaveNew}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={handleCancelNew}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Existing Rows */}
                  {filteredData.map((row, index) => (
                    <TableRow 
                      key={index} 
                      hover 
                      selected={selectedRows.includes(index)}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(index)}
                          onChange={(e) => handleRowSelect(e, index)}
                        />
                      </TableCell>
                      <TableCell>
                        {editingId === index ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.date}
                            onChange={(e) => handleInputChange(e, index, "date")}
                          />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {row.date}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === index ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.inTime}
                            onChange={(e) => handleInputChange(e, index, "inTime")}
                          />
                        ) : (
                          row.inTime
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === index ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.outTime}
                            onChange={(e) => handleInputChange(e, index, "outTime")}
                          />
                        ) : (
                          row.outTime
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === index ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.workedHours}
                            onChange={(e) => handleInputChange(e, index, "workedHours")}
                          />
                        ) : (
                          <Chip 
                            label={`${row.workedHours}h`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === index ? (
                          <Select
                            fullWidth
                            size="small"
                            value={row.attendanceStatus}
                            onChange={(e) => handleInputChange(e, index, "attendanceStatus")}
                          >
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                            <MenuItem value="Late">Late</MenuItem>
                            <MenuItem value="Leave">Leave</MenuItem>
                          </Select>
                        ) : (
                          <Chip 
                            label={row.attendanceStatus} 
                            size="small"
                            color={getStatusColor(row.attendanceStatus)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === index ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.projectId}
                            onChange={(e) => handleInputChange(e, index, "projectId")}
                          />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {row.projectId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {editingId === index ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton size="small" color="primary" onClick={() => handleSave(index)}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" onClick={handleCancel}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => handleViewDetails(row)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton size="small" color="primary" onClick={() => handleEdit(index)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredData.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body1">
                  No records found matching your criteria
                </Typography>
              </Box>
            )}
          </Card>
        )}

        {/* Action Buttons */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" },
          gap: 2, 
          justifyContent: "center",
          mt: 4,
          mb: 2
        }}>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: "#29BF2E",
              borderRadius: "20px",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              minWidth: { xs: "100%", sm: 140 },
              "&:hover": { bgcolor: "#22A026" },
            }}
          >
            Submit
          </Button>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: "#D32F2F",
              borderRadius: "20px",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              minWidth: { xs: "100%", sm: 140 },
              "&:hover": { bgcolor: "#C62828" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => setSelectedSection("Leaves")}
            sx={{
              bgcolor: "#7B4E86",
              borderRadius: "20px",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              minWidth: { xs: "100%", sm: 140 },
              "&:hover": { bgcolor: "#6A4274" },
            }}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Dialog for Mobile View/Details */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentRow ? "Attendance Details" : "Add New Record"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {currentRow ? (
              // View Mode
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  {[
                    { label: "Date", value: currentRow.date, icon: <CalendarIcon /> },
                    { label: "In Time", value: currentRow.inTime, icon: <ScheduleIcon /> },
                    { label: "Out Time", value: currentRow.outTime, icon: <ScheduleIcon /> },
                    { label: "Worked Hours", value: `${currentRow.workedHours}h`, icon: <AccessTimeIcon /> },
                    { label: "Status", value: currentRow.attendanceStatus, icon: <CheckCircleIcon /> },
                    { label: "Project ID", value: currentRow.projectId, icon: <WorkIcon /> },
                    { label: "Manager", value: currentRow.manager, icon: <PersonIcon /> },
                    { label: "Billable Days", value: currentRow.billableDays, icon: <CalendarIcon /> },
                  ].map((item, index) => (
                    <Grid item xs={6} key={index}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.label}
                      </Typography>
                      <Typography variant="body1" fontWeight={500} display="flex" alignItems="center" gap={1}>
                        {item.icon}
                        {item.value}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Remarks
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {currentRow.remarks || "No remarks"}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              // Add/Edit Mode
              <Stack spacing={2}>
                {[
                  { field: "date", label: "Date", type: "date" },
                  { field: "inTime", label: "In Time", type: "time" },
                  { field: "outTime", label: "Out Time", type: "time" },
                  { field: "workedHours", label: "Worked Hours", type: "number" },
                  { field: "attendanceStatus", label: "Status", type: "select" },
                  { field: "projectId", label: "Project ID", type: "text" },
                  { field: "manager", label: "Manager", type: "text" },
                  { field: "remarks", label: "Remarks", type: "text", multiline: true, rows: 3 },
                ].map((item) => (
                  item.type === "select" ? (
                    <FormControl key={item.field} fullWidth>
                      <InputLabel>{item.label}</InputLabel>
                      <Select
                        value={newRow[item.field]}
                        onChange={(e) => handleInputChangeNew(e, item.field)}
                        label={item.label}
                      >
                        <MenuItem value="Present">Present</MenuItem>
                        <MenuItem value="Absent">Absent</MenuItem>
                        <MenuItem value="Late">Late</MenuItem>
                        <MenuItem value="Leave">Leave</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      key={item.field}
                      label={item.label}
                      type={item.type}
                      value={newRow[item.field]}
                      onChange={(e) => handleInputChangeNew(e, item.field)}
                      fullWidth
                      multiline={item.multiline}
                      rows={item.rows}
                      InputLabelProps={item.type === "date" ? { shrink: true } : {}}
                    />
                  )
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {currentRow ? "Close" : "Cancel"}
          </Button>
          {!currentRow && (
            <Button 
              variant="contained" 
              onClick={handleSaveNew}
              startIcon={<SaveIcon />}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance1;